import type { SpecToolDecision } from '@parcel/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  TooltipProvider,
  cn,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react';
import * as React from 'react';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import {
  DECISION_LABELS,
  DECISION_STRICTNESS,
  type SpecToolGroupPolicyFailure,
  type SpecToolGroupPolicyPreview,
  SpecToolPolicyError,
  type SpecToolSettingsRow,
  applyWorkspaceToolGroupPolicy,
  listSpecTools,
  previewWorkspaceToolGroupPolicy,
  updateMyToolPolicy,
  updateWorkspaceToolPolicy,
} from '../../lib/spec-tools.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { SettingsPageHeader, SettingsShell } from './settings-shell.js';
import { ListSkeleton } from './settings-skeletons.js';
import { SpecToolRow } from './spec-tool-row.js';

/**
 * Settings > Spec AI > Tools.
 *
 * Owns every piece of server state on this page: `SpecToolRow` and
 * `SpecToolPolicyControl` are pure, so all fetching, saving, conflict handling
 * and refetching lives here.
 *
 * CONNECTION AND SKILL TOOLS MUST NEVER APPEAR HERE. There is deliberately no
 * filter enforcing that: `GET /workspaces/:id/spec/tools` serves the native
 * registry and nothing else, and this page rendering exactly the rows it was
 * given IS the guarantee. Do not merge another source (connections, skills, a
 * local catalog) into this list -- add a separate page instead.
 */

/**
 * Registry effect -> display label, in render order.
 *
 * Grouped by what a tool DOES, not by which domain it belongs to. The setting
 * on this page is a risk decision ("may Spec run this without asking"), and
 * risk is what `effect` encodes: every tool in a group shares a safety floor
 * and answers the same question, so a group-level default is meaningful rather
 * than a coincidence. Grouping by domain instead put `search_accounts` and
 * `workspace_delete_account` under one "Data" heading with one bulk control,
 * where no single answer is right for both.
 *
 * An effect missing from this map still renders, under its raw name, after all
 * the known ones: a new registry effect must never make tools silently vanish
 * from the page a workspace uses to govern them.
 *
 * One exception, {@link specToolGroupKey}: enrichment spends show with the reads, held at their floor.
 */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  read: 'Read-only tools',
  reversible_write: 'Write tools',
  // "Delete", not the registry's own word "destructive": every tool in this
  // group deletes a specific thing, and that is both plainer and more accurate
  // about what an admin is deciding.
  destructive: 'Delete tools',
  admin_security: 'Admin and security tools',
};

const GROUP_ORDER: readonly string[] = Object.keys(GROUP_LABELS);

const DECISION_CHOICES: readonly SpecToolDecision[] = ['always_allow', 'needs_approval', 'blocked'];

/**
 * What a group's dropdown reads when its tools do not agree.
 *
 * Not selectable: picking "Custom" would have no defined meaning. It is a
 * readout of the rows, so an admin can see at a glance which groups have been
 * hand-tuned away from a single posture.
 */
const MIXED_GROUP_LABEL = 'Custom';

const REVISION_CONFLICT_MESSAGE =
  'This tool changed since you loaded the page. The current value is shown now -- try again if you still want your change.';

export interface SpecToolGroup {
  /** The registry `effect` every row in this group shares. */
  effect: string;
  label: string;
  rows: SpecToolSettingsRow[];
}

/**
 * The one decision a whole group is currently at, or `null` when its tools
 * disagree.
 *
 * Measured on the workspace's EFFECTIVE posture per row (`workspaceDecision`
 * when set, otherwise the tool's own floor), not on the stored column. A group
 * nobody has ever configured is not "unset", it is sitting at its floors, and
 * reading it as unset would show every untouched group as Custom on first load.
 */
export function uniformGroupDecision(
  rows: readonly SpecToolSettingsRow[],
): SpecToolDecision | null {
  const postures = rows.map((row) => row.workspaceDecision ?? row.registryFloor);
  // A row nobody set, held at a floor stricter than the group's setting, still reads as that setting.
  const fits = (candidate: SpecToolDecision) =>
    rows.every(
      (row, i) =>
        postures[i] === candidate ||
        (row.workspaceDecision === null &&
          DECISION_STRICTNESS[candidate] < DECISION_STRICTNESS[row.registryFloor]),
    );
  return [...new Set(postures)].find(fits) ?? null;
}

/** Enrichment reads data the workspace pays for, so it sits with the reads whatever its floor. */
export function specToolGroupKey(row: Pick<SpecToolSettingsRow, 'category' | 'effect'>): string {
  return row.category === 'enrich' ? 'read' : row.effect;
}

/** Case-insensitive match on the three fields a member would search by. */
export function matchesSpecToolSearch(row: SpecToolSettingsRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    row.title.toLowerCase().includes(needle) ||
    row.name.toLowerCase().includes(needle) ||
    row.description.toLowerCase().includes(needle)
  );
}

/**
 * Known effects in {@link GROUP_ORDER}, then any unknown effect in the order
 * the API first mentioned it. Empty groups are dropped, so a search that
 * matches nothing in a group hides that group's header too.
 */
export function groupSpecToolRows(rows: readonly SpecToolSettingsRow[]): SpecToolGroup[] {
  const byEffect = new Map<string, SpecToolSettingsRow[]>();
  for (const row of rows) {
    const key = specToolGroupKey(row);
    const bucket = byEffect.get(key);
    if (bucket) bucket.push(row);
    else byEffect.set(key, [row]);
  }

  const groups: SpecToolGroup[] = [];
  for (const effect of GROUP_ORDER) {
    const bucket = byEffect.get(effect);
    if (!bucket || bucket.length === 0) continue;
    byEffect.delete(effect);
    groups.push({ effect, label: GROUP_LABELS[effect] ?? effect, rows: bucket });
  }
  for (const [effect, bucket] of byEffect) {
    if (bucket.length === 0) continue;
    groups.push({ effect, label: effect, rows: bucket });
  }
  return groups;
}

type GroupDraft = {
  effect: string;
  label: string;
  decision: SpecToolDecision;
  preview: SpecToolGroupPolicyPreview;
  /** True when the preview was computed over a search-narrowed set of rows. */
  narrowedBySearch: boolean;
};

type GroupOutcome = {
  label: string;
  decision: SpecToolDecision;
  updated: number;
  failures: readonly SpecToolGroupPolicyFailure[];
};

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Per-row transient state (in-flight write, inline error) belongs to ONE policy
 * layer, so it is keyed by scope as well as tool.
 *
 * Keying by tool alone would re-attach a workspace-layer message to the same
 * tool's personal control the moment a manager switched to `My overrides`: a
 * member reading "this changed since you loaded the page" on their own override
 * would conclude their override had been changed by someone else, when the
 * conflict was on a layer they cannot even edit. On a page whose whole job is
 * telling a member which layer bound a decision, an error attached to the wrong
 * control is worse than no error.
 */
function policyStateKey(scope: 'workspace' | 'member', toolName: string): string {
  return `${scope}:${toolName}`;
}

/**
 * Settings > Spec AI > Tools, behind the same visibility switch the sidebar and
 * Spec's own route already use.
 *
 * The guard is not cosmetic. Both `/settings/spec-ai/tools` and
 * `/{slug}/settings/spec-ai/tools` are real registered routes, so a bookmark or
 * a typed URL reaches this page even where the sidebar hides Spec entirely. On
 * a dark-launch workspace every row would then read "Effective: Blocked" over
 * "Spec is turned off for this workspace right now, so no tool can run" -- true
 * of Spec, and false of the surface the write actually governs:
 * `workspace_tool_policies` is surface-INDEPENDENT (see the docstring atop
 * apps/api/src/lib/spec/policy.ts) and the Developer MCP Worker reads these
 * exact rows as its LIVE authorization context (apps/mcp/src/grant-context.ts).
 * Setting a tool to Blocked "on a feature that can't run" would disable it for
 * every MCP client in the workspace within that reader's cache window.
 *
 * The API deliberately does not gate policy writes on launch state, because
 * pre-configuring before launch has to work, which leaves this page as the only
 * place the gate can live.
 *
 * Split in two so the guard sits above every hook the body owns: flipping
 * visibility must not change how many hooks this component renders.
 */
export function SpecToolsSettingsPage() {
  const launchState = useSpecLaunchState();
  if (!isSpecSurfaceVisible(launchState)) return <SpecToolsNotAvailable />;
  return <SpecToolsSettingsContent />;
}

function SpecToolsNotAvailable() {
  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Spec AI"
        title="Tools"
        description="Spec is not enabled for this workspace yet."
      />
      <div
        data-testid="spec-tools-not-available"
        className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground"
      >
        Once Spec is enabled here, this is where owners and admins choose how much autonomy it has
        for each Parcel tool.
      </div>
    </SettingsShell>
  );
}

function SpecToolsSettingsContent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const queryClient = useQueryClient();

  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug),
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id ?? null;
  const canManage = workspaceQuery.data?.capabilities.canManageSpecTools ?? false;

  // Audit counts are owner/admin-only server-side and cost an extra query, so a
  // member's request must not ask for them. `canManage` is part of the key: a
  // member and a manager are asking two different questions of the same URL.
  const toolsQuery = useQuery({
    queryKey: ['spec-tools', workspaceId, canManage],
    enabled: Boolean(workspaceId),
    queryFn: () => listSpecTools(workspaceId ?? '', { includeAuditCounts: canManage }),
  });

  const [search, setSearch] = React.useState('');
  const [scope, setScope] = React.useState<'workspace' | 'member'>('workspace');
  // Both keyed by `policyStateKey(scope, toolName)`, never by tool alone.
  // A set, not one "currently saving" name: each tool saves independently, so
  // two in-flight writes must each show their own spinner rather than one
  // stealing the other's.
  const [savingTools, setSavingTools] = React.useState<ReadonlySet<string>>(() => new Set());
  const [rowErrors, setRowErrors] = React.useState<Record<string, string | null>>({});
  const [groupDraft, setGroupDraft] = React.useState<GroupDraft | null>(null);
  const [applyingGroup, setApplyingGroup] = React.useState(false);
  const [groupError, setGroupError] = React.useState<string | null>(null);
  const [groupOutcome, setGroupOutcome] = React.useState<GroupOutcome | null>(null);
  // Collapsed rather than expanded, so the default state is "everything
  // visible": a group a search just matched must never be hidden behind a
  // disclosure the member did not close.
  const [collapsedGroups, setCollapsedGroups] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const toggleGroup = (effect: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(effect)) next.delete(effect);
      else next.add(effect);
      return next;
    });
  };

  // A member has no workspace layer to edit, so the page is always their own
  // overrides. Managers land on the workspace layer and can switch.
  const activeScope: 'workspace' | 'member' = canManage ? scope : 'member';

  // Held as one object rather than destructured field by field: `auditWindowDays`
  // is only meaningful next to the `recentAuditCount` values from the SAME
  // response, and narrowing this one binding is what lets a row be handed both
  // without inventing a default window.
  const settings = toolsQuery.data ?? null;
  const tools = React.useMemo(() => settings?.tools ?? [], [settings]);
  const visibleRows = React.useMemo(
    () => tools.filter((row) => matchesSpecToolSearch(row, search)),
    [tools, search],
  );
  const groups = React.useMemo(() => groupSpecToolRows(visibleRows), [visibleRows]);
  const searchActive = search.trim().length > 0;

  // Every view of the list, the composer's Web switch (member key) included, so none shows a stale policy.
  const refetchTools = React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['spec-tools', workspaceId] }),
    [queryClient, workspaceId],
  );

  const setRowError = (key: string, message: string | null) => {
    setRowErrors((current) => ({ ...current, [key]: message }));
  };

  const markSaving = (key: string, saving: boolean) => {
    setSavingTools((current) => {
      const next = new Set(current);
      if (saving) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  /**
   * Switching layers also drops the bulk-apply feedback. Both banners describe
   * a workspace-layer action that was just taken from the workspace view; a
   * stale "Set 4 tools in Data..." sitting above a member's own overrides would
   * claim their overrides had been rewritten.
   */
  const switchScope = (next: 'workspace' | 'member') => {
    setScope(next);
    setGroupOutcome(null);
    setGroupError(null);
  };

  /**
   * One tool, one write, no form-wide dirty state.
   *
   * Nothing here writes the chosen value into local state: every control reads
   * its value straight off the last server response, so a rejected write can
   * never leave the control displaying a value the server refused.
   */
  const saveTool = async (row: SpecToolSettingsRow, decision: SpecToolDecision | null) => {
    const key = policyStateKey(activeScope, row.name);
    if (!workspaceId || savingTools.has(key)) return;
    // There is no "clear" affordance on the workspace layer: a workspace always
    // has a decision, so a null here would be a caller bug, not a write.
    if (activeScope === 'workspace' && decision === null) return;
    markSaving(key, true);
    setRowError(key, null);
    try {
      if (activeScope === 'workspace' && decision !== null) {
        await updateWorkspaceToolPolicy(workspaceId, row.name, decision, row.revision);
      } else {
        await updateMyToolPolicy(workspaceId, row.name, decision);
      }
      await refetchTools();
    } catch (err) {
      if (err instanceof SpecToolPolicyError && err.code === 'revision_conflict') {
        setRowError(key, REVISION_CONFLICT_MESSAGE);
        await refetchTools();
      } else {
        setRowError(key, errorMessage(err, 'Unable to save this tool.'));
      }
    } finally {
      markSaving(key, false);
    }
  };

  const openGroupDraft = (group: SpecToolGroup, decision: SpecToolDecision) => {
    setGroupError(null);
    setGroupOutcome(null);
    setGroupDraft({
      effect: group.effect,
      label: group.label,
      decision,
      // Previewed over the rows on screen, so the confirmation can never name a
      // tool a search has hidden.
      preview: previewWorkspaceToolGroupPolicy(group.rows, decision),
      narrowedBySearch: searchActive,
    });
  };

  const applyGroupDraft = async () => {
    if (!workspaceId || !groupDraft) return;
    // An empty applicable set means there is nothing to write. Calling the
    // batch client here would POST `{inputs: []}`.
    if (groupDraft.preview.applicable.length === 0) return;
    setApplyingGroup(true);
    setGroupError(null);
    try {
      const expectedRevisions: Record<string, number> = {};
      for (const row of groupDraft.preview.applicable) expectedRevisions[row.name] = row.revision;
      const result = await applyWorkspaceToolGroupPolicy(workspaceId, {
        decision: groupDraft.decision,
        expectedRevisions,
      });
      // Partial success is the normal case: report what landed and what did
      // not, per tool, rather than collapsing it into one verdict.
      setGroupOutcome({
        label: groupDraft.label,
        decision: groupDraft.decision,
        updated: result.updated.length,
        failures: result.failures,
      });
      setGroupDraft(null);
      await refetchTools();
    } catch (err) {
      setGroupError(errorMessage(err, 'Unable to apply the group default.'));
    } finally {
      setApplyingGroup(false);
    }
  };

  const listError = workspaceQuery.error ?? toolsQuery.error;
  const isLoading =
    (Boolean(workspaceSlug) && workspaceQuery.isPending) ||
    (Boolean(workspaceId) && toolsQuery.isPending);

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Spec AI"
        title="Tools"
        description="Choose the most autonomy Spec may have for each Parcel tool: run it on its own, ask you to approve first, or block it outright. Some tools have a built-in safety floor that no setting here can lower."
      />

      <div className="space-y-6">
        {/*
          Underline tabs, matching the saved-view tabs in `ViewTabs`
          (packages/ui/src/components/view-tabs.tsx), whose classes these
          mirror. A layer switch is navigation between two views of the same
          list, which is what a tab means here and what a button did not.
          Deliberately not `ViewTabs` itself: that component carries saved-view
          baggage (rename, save-as-new, dirty state) that has no meaning here.

          A member never sees the tabs at all. They have no workspace layer to
          edit, so the choice would only be offering a dead end.
        */}
        {canManage ? (
          <div
            className="flex items-center border-b"
            role="tablist"
            aria-label="Which tools policy you are editing"
          >
            {(
              [
                ['workspace', 'Workspace'],
                ['member', 'Personal'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={scope === value}
                className={cn(
                  'relative h-10 min-w-16 shrink-0 px-2 text-sm font-medium text-muted-foreground hover:text-foreground',
                  scope === value &&
                    'text-foreground after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:bg-foreground',
                )}
                onClick={() => switchScope(value)}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        <p className="text-sm text-muted-foreground">
          {activeScope === 'workspace'
            ? 'Workspace defaults, applied to everyone. Members can make any tool stricter for themselves.'
            : canManage
              ? 'Your own settings, on top of the workspace defaults. As an admin you can loosen these for yourself, down to each tool\u2019s safety floor.'
              : 'Your workspace sets these defaults. You can make any tool stricter for yourself.'}
        </p>

        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            aria-label="Search tools"
            placeholder="Search tools"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>

        {/*
          `<output>`, whose implicit role is `status`, so this is a live region:
          it is the ONLY report that a bulk apply partially failed, and it
          appears after the confirmation dialog has closed and focus has
          returned to the trigger. Without the announcement a screen reader
          would never hear that 2 of 6 tools conflicted. Same element
          `SpecToolPolicyControl` uses for its saving indicator.
        */}
        {groupOutcome ? (
          <output
            className="block space-y-1 rounded-lg border p-4 text-sm"
            data-testid="group-apply-result"
          >
            <p>
              {groupOutcome.updated === 1
                ? `Set 1 tool in ${groupOutcome.label} to "${DECISION_LABELS[groupOutcome.decision]}".`
                : `Set ${groupOutcome.updated} tools in ${groupOutcome.label} to "${DECISION_LABELS[groupOutcome.decision]}".`}
            </p>
            {groupOutcome.failures.map((failure) => (
              <p key={failure.toolName} className="text-destructive">
                {failure.toolName}: {failure.message}
              </p>
            ))}
          </output>
        ) : null}

        {listError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(listError, 'Unable to load Spec tools.')}
          </p>
        ) : isLoading ? (
          <ListSkeleton rows={4} className="rounded-lg border" />
        ) : !settings || tools.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
            No Spec tools are available for this workspace.
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
            No tools match "{search.trim()}".
          </div>
        ) : (
          /*
            One provider for the whole list, not one per row: 36 rows would
            otherwise each mount their own Radix delay and skip-delay timer
            group, and hovering along the list would restart the delay on every
            single row instead of opening instantly after the first.
          */
          <TooltipProvider delayDuration={200}>
            <div className="divide-y rounded-lg border">
              {groups.map((group) => {
                const collapsed = collapsedGroups.has(group.effect);
                const uniform = uniformGroupDecision(group.rows);
                const bodyId = `spec-tool-group-body-${group.effect}`;
                return (
                  <section key={group.effect} aria-labelledby={`spec-tool-group-${group.effect}`}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <button
                        type="button"
                        className="flex min-w-0 items-center gap-2 text-left"
                        aria-expanded={!collapsed}
                        aria-controls={bodyId}
                        onClick={() => toggleGroup(group.effect)}
                      >
                        {collapsed ? (
                          <ChevronRight
                            className="size-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                        ) : (
                          <ChevronDown
                            className="size-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                        )}
                        <h3
                          id={`spec-tool-group-${group.effect}`}
                          className="truncate text-sm font-medium"
                        >
                          {group.label}
                        </h3>
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          {group.rows.length}
                        </span>
                      </button>
                      {canManage && activeScope === 'workspace' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="shrink-0 font-normal"
                              aria-label={`Set every tool in ${group.label}`}
                            >
                              {uniform ? DECISION_LABELS[uniform] : MIXED_GROUP_LABEL}
                              <ChevronDown className="size-4" aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                              Apply to every tool whose floor allows it
                            </DropdownMenuLabel>
                            {DECISION_CHOICES.map((decision) => (
                              <DropdownMenuItem
                                key={decision}
                                onClick={() => openGroupDraft(group, decision)}
                              >
                                <span className="flex-1">{DECISION_LABELS[decision]}</span>
                                {uniform === decision ? (
                                  <Check className="size-4" aria-hidden />
                                ) : null}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </div>
                    {collapsed ? null : (
                      <div id={bodyId} className="divide-y border-t px-4">
                        {group.rows.map((row) => (
                          <SpecToolRow
                            key={row.name}
                            row={row}
                            mode={activeScope}
                            auditWindowDays={settings.auditWindowDays}
                            outranksWorkspace={canManage}
                            saving={savingTools.has(policyStateKey(activeScope, row.name))}
                            error={rowErrors[policyStateKey(activeScope, row.name)] ?? null}
                            onChange={(decision) => void saveTool(row, decision)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>

      <Dialog
        open={Boolean(groupDraft)}
        onOpenChange={(open) => {
          // Every dismissal path funnels through here -- Escape, an overlay
          // click, and DialogContent's own close button -- and all three are
          // refused while an apply is in flight. Closing mid-flight would
          // unmount `groupDraft`, and with it the ONLY place that request's
          // failure can be reported, so the write would fail in silence.
          // Nothing else re-checks: `applyGroupDraft` captured its draft at
          // click time and cannot notice the dialog going away underneath it.
          if (open || applyingGroup) return;
          setGroupDraft(null);
          setGroupError(null);
        }}
      >
        <DialogContent>
          {groupDraft ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {`Set ${groupDraft.label} to "${DECISION_LABELS[groupDraft.decision]}"?`}
                </DialogTitle>
                <DialogDescription>
                  {`${groupDraft.preview.applicable.length} will change, ${groupDraft.preview.unchanged.length} already at this setting, ${groupDraft.preview.exceptions.length} cannot be changed.`}
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-80 space-y-4 overflow-auto text-sm">
                {groupDraft.narrowedBySearch ? (
                  <p className="text-muted-foreground">
                    Only the tools matching your current search are included.
                  </p>
                ) : null}
                {groupDraft.preview.applicable.length > 0 ? (
                  <div className="space-y-1">
                    <p className="font-medium">Will change</p>
                    <ul className="space-y-0.5 text-muted-foreground">
                      {groupDraft.preview.applicable.map((row) => (
                        <li key={row.name}>{row.title}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {groupDraft.preview.unchanged.length > 0 ? (
                  <div className="space-y-1">
                    <p className="font-medium">Already at this setting</p>
                    <ul className="space-y-0.5 text-muted-foreground">
                      {groupDraft.preview.unchanged.map((row) => (
                        <li key={row.name}>{row.title}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {groupDraft.preview.exceptions.length > 0 ? (
                  <div className="space-y-1">
                    <p className="font-medium">Cannot be changed</p>
                    <ul className="space-y-0.5 text-muted-foreground">
                      {groupDraft.preview.exceptions.map((exception) => (
                        <li key={exception.toolName}>{exception.reason}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              {/*
                Rendered INSIDE the dialog, not in the page body. A failed apply
                leaves this dialog open, and a Radix modal `aria-hidden`s and
                overlays everything behind it: an alert in the page body would
                be neither visible nor announced, so the admin would see the
                dialog sit there unchanged and click Apply again into the same
                silence.
              */}
              {groupError ? (
                <p role="alert" className="text-sm text-destructive">
                  {groupError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={applyingGroup}
                  onClick={() => {
                    setGroupDraft(null);
                    setGroupError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={applyingGroup || groupDraft.preview.applicable.length === 0}
                  onClick={() => void applyGroupDraft()}
                >
                  {applyingGroup ? 'Applying…' : 'Apply default'}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </SettingsShell>
  );
}
